// Quick guide — Advanced Word Order (A2). The basic guide covers Subject-Verb-
// Object; this one adds the pieces that trip learners up once sentences grow:
// where adjectives sit, how object pronouns jump in front of the verb, the
// indirect-before-direct order, and the le/les → se swap. Hand-drawn paper
// style with audio on every example.
import { speakButtonHTML } from "../lib/audio.js";

// One worked example: the Spanish (with the moving part in <b>), an English
// gloss, and an optional note. Audio reads the plain Spanish.
function ex(es, en, note) {
  const plain = es.replace(/<[^>]+>/g, "");
  return `
    <div class="mt-2">
      <p class="text-sage-800">${es}${speakButtonHTML(plain)} <span class="text-base text-sage-500" style="font-family:Inter,sans-serif">— ${en}</span></p>
      ${note ? `<p class="text-sm text-sage-500" style="font-family:Inter,sans-serif">${note}</p>` : ""}
    </div>`;
}

export function advancedWordOrderView(app) {
  app.innerHTML = `
    <section class="max-w-3xl mx-auto fade-in">
      <h2 class="text-4xl font-script text-sage-800 mb-1">Advanced Word Order 🪡</h2>
      <p class="text-sage-600 mb-4" style="font-family:Inter,sans-serif">Once your sentences carry an object, a pronoun, or a description, Spanish puts the pieces in places English doesn't. These are the four patterns that matter most at A2.</p>

      <div class="paper sketch-box p-5 sm:p-7 font-hand text-lg space-y-5">

        <div>
          <h3 class="text-2xl text-emerald-700 sketch-underline inline-block">1 · Adjectives go after the noun</h3>
          <p class="text-sage-700 mt-1">The default is <b>noun + adjective</b> — the opposite of English — and the adjective agrees in gender and number.</p>
          ${ex("un coche <b>rojo</b>", "a red car")}
          ${ex("las casas <b>blancas</b>", "the white houses", "Plural noun → plural adjective.")}
          <p class="text-sage-700 mt-3">A few short, common ones go <i>before</i>: <b>bueno, malo, grande, pequeño</b>, and numbers / quantities.</p>
          ${ex("un <b>buen</b> amigo · <b>muchos</b> libros", "a good friend · many books")}
          <p class="text-sage-700 mt-3">And a handful change meaning by position:</p>
          <div class="flex flex-wrap gap-x-6 gap-y-1 mt-1 text-base">
            <span><b>un gran hombre</b> a great man · <b>un hombre grande</b> a big man</span>
            <span><b>un viejo amigo</b> a longtime friend · <b>un amigo viejo</b> an elderly friend</span>
          </div>
        </div>

        <div>
          <h3 class="text-2xl text-amber-700 sketch-underline inline-block">2 · Object pronouns jump in front of the verb</h3>
          <p class="text-sage-700 mt-1">Unlike English, the object pronoun (me, te, lo, la, nos, los, le, les) sits <b>before</b> the conjugated verb.</p>
          ${ex("<b>Lo</b> veo.", "I see it / him.", "Not “veo lo”.")}
          ${ex("María <b>me</b> llama.", "María calls me.")}
          <p class="text-sage-700 mt-3">With an infinitive or an -ing form, you may instead <b>attach it to the end</b> — both are correct:</p>
          ${ex("<b>Te</b> quiero ver. = Quiero ver<b>te</b>.", "I want to see you.")}
          ${ex("<b>Lo</b> estoy haciendo. = Estoy hacién<b>dolo</b>.", "I'm doing it.", "Attaching often adds a written accent to keep the stress.")}
        </div>

        <div>
          <h3 class="text-2xl text-sky-700 sketch-underline inline-block">3 · Two pronouns: indirect before direct</h3>
          <p class="text-sage-700 mt-1">When you have both — <i>to whom</i> and <i>what</i> — the order is <b>indirect + direct + verb</b>.</p>
          ${ex("Ella <b>me lo</b> da.", "She gives it to me.", "me = to me (indirect) · lo = it (direct).")}
          ${ex("¿<b>Te los</b> mando?", "Shall I send them to you?")}
        </div>

        <div>
          <h3 class="text-2xl text-rose-600 sketch-underline inline-block">4 · le / les become se before lo/la/los/las</h3>
          <p class="text-sage-700 mt-1">When the indirect <b>le</b> or <b>les</b> meets a direct <b>lo/la/los/las</b>, it changes to <b>se</b> (Spanish avoids “le lo”).</p>
          ${ex("le + lo → <b>se lo</b> digo", "I tell it to him/her", "Not “le lo digo”.")}
          ${ex("<b>Se las</b> di a ellos.", "I gave them to them.", "se can mean to him, to her, to them — add “a él / a ellos” to be clear.")}
        </div>

        <div>
          <h3 class="text-2xl text-violet-700 sketch-underline inline-block">5 · Where “no” and adverbs sit</h3>
          <p class="text-sage-700 mt-1"><b>No</b> (and any pronouns) come as a block right before the verb.</p>
          ${ex("<b>No</b> me lo da.", "He doesn't give it to me.", "no → pronouns → verb, all together.")}
          ${ex("Siempre <b>llego</b> tarde.", "I always arrive late.", "Frequency adverbs sit near the verb; tarde/temprano often at the end.")}
        </div>

      </div>
      <p class="text-xs text-sage-400 mt-4" style="font-family:Inter,sans-serif">Rule of thumb: pronouns cling to the verb (in front of a conjugated one, or stuck on the end of an infinitive / -ing form), and “to-whom” always comes before “what”.</p>
    </section>`;
  window.scrollTo({ top: 0, behavior: "smooth" });
}
